import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, BusinessKeywordChange } from '../utils/transactionStore';
const MakerBusinessKeywordTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pending, setPending] = useState<BusinessKeywordChange[]>([]);
  const [rejected, setRejected] = useState<BusinessKeywordChange[]>([]);
  const [selected, setSelected] = useState<BusinessKeywordChange | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resubmit' | 'discard' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => {
    setPending(transactionStore.getPendingBusinessKeywordChanges());
    setRejected(transactionStore.getRejectedBusinessKeywordChanges());
  };
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: BusinessKeywordChange, action: 'resubmit' | 'discard') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'resubmit') {
        transactionStore.resubmitBusinessKeywordChange(selected.id, reason);
        showToast(`Keyword request resubmitted for "${selected.keyword}"`, 'success');
      } else {
        transactionStore.discardBusinessKeywordChange(selected.id, reason);
        showToast(`Keyword request discarded for "${selected.keyword}"`, 'info');
      }
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const pendingColumns: Column<BusinessKeywordChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1278" data-mp="tdp1wmon6nl4">{row.id}</Typography>
  }, {
    id: 'action',
    label: 'Action',
    renderCell: row => <Chip label={row.action} size="small" sx={{
      fontWeight: 700,
      bgcolor: row.action === 'ADD' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
      color: row.action === 'ADD' ? '#059669' : '#EF4444'
    }} data-id="element-1279" data-mp="p016ori0k9w" />
  }, {
    id: 'keyword',
    label: 'Keyword',
    renderCell: row => <Typography variant="body2" fontWeight={700} sx={{
      letterSpacing: 0.5
    }} data-id="element-1280" data-mp="ahfeh8ruokog">{row.keyword}</Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: () => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1281" data-mp="3e3efkwdm03c8" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1282" data-mp="1r8rdq8yupyik">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  const rejectedColumns: Column<BusinessKeywordChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1283" data-mp="1cbiiv5acacm3">{row.id}</Typography>
  }, {
    id: 'action',
    label: 'Action',
    renderCell: row => <Chip label={row.action} size="small" sx={{
      fontWeight: 700
    }} data-id="element-1284" data-mp="13i5iji9y48pz" />
  }, {
    id: 'keyword',
    label: 'Keyword',
    renderCell: row => <Typography variant="body2" fontWeight={700} data-id="element-1285" data-mp="ykdghvfl7682">{row.keyword}</Typography>
  }, {
    id: 'checkerComment',
    label: 'Rejection Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="error.main" noWrap sx={{
      maxWidth: 200
    }} data-id="element-1286" data-mp="tm271lt7exqd">{row.checkerComment}</Typography>
  }, {
    id: 'checkerActedAt',
    label: 'Rejected At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1287" data-mp="2opqhlc8poklx">{new Date(row.checkerActedAt || '').toLocaleString()}</Typography>
  }];
  const rejectedRowActions: RowAction<BusinessKeywordChange>[] = [{
    icon: 'lucide:rotate-cw',
    label: 'Resubmit',
    onClick: row => handleAction(row, 'resubmit')
  }, {
    icon: 'lucide:trash-2',
    label: 'Delete',
    onClick: row => handleAction(row, 'discard')
  }];
  return <Box data-id="element-1288" data-mp="2aw97sj5tum93">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1289" data-mp="2ax0qitebsphy">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} data-id="element-1290" data-mp="1vyyo4mj16a2a">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1292" data-mp="1w12xhjzp819y"><Typography variant="body2" fontWeight={500} data-id="element-1293" data-mp="20yl3p2mc02e9">Pending</Typography><Chip label={pending.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B'
          }} data-id="element-1294" data-mp="3qc81wqim0hqc" /></Box>} data-id="element-1291" data-mp="1w0tc69plmial" />
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1296" data-mp="1joh3rstjqhxy"><Typography variant="body2" fontWeight={500} data-id="element-1297" data-mp="1jp0lb8fab4bg">Rejected</Typography><Chip label={rejected.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444'
          }} data-id="element-1298" data-mp="1epwbqgog74kl" /></Box>} data-id="element-1295" data-mp="3v9gkutbl0psn" />
        </Tabs>
      </Box>

      {activeTab === 0 ? <DataTable title="Pending Business Keyword Requests" data={pending} columns={pendingColumns} keyField="id" emptyStateMessage="No pending business keyword requests" initialVisibleColumns={['action', 'keyword', 'status', 'requestedAt']} data-id="element-1299" data-mp="1omiw74cv3tm3" /> : <DataTable title="Rejected Business Keyword Requests" data={rejected} columns={rejectedColumns} rowActions={rejectedRowActions} keyField="id" emptyStateMessage="No rejected business keyword requests" initialVisibleColumns={['action', 'keyword', 'checkerComment', 'checkerActedAt']} data-id="element-1300" data-mp="1yfpz8ik4qxj0" />}

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1301" data-mp="i0yij2wcjepj">
        <DialogTitle data-id="element-1302" data-mp="2d2jtbhkwfu36">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1303" data-mp="2d31tuqelgugw">
            <Icon icon={actionType === 'resubmit' ? 'lucide:rotate-cw' : 'lucide:trash-2'} width={24} color={actionType === 'resubmit' ? '#0EA5E9' : '#EF4444'} data-id="element-1304" data-mp="2i13evgvkz3v0" />
            <Typography variant="h6" fontWeight={600} data-id="element-1305" data-mp="2m1nd6nrw4diq">{actionType === 'resubmit' ? 'Resubmit' : 'Delete'} Keyword Request</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1306" data-mp="2w036mxee3yve">
          <Box sx={{
          mt: 1
        }} data-id="element-1307" data-mp="2w0w395jmx7zt">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-1308" data-mp="30z7f1lthpsko">
                  {selected.action === 'ADD' ? 'Add' : 'Delete'} Keyword: <strong data-id="element-1309" data-mp="35tbkg0n3xsjn">{selected.keyword}</strong>
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-1310" data-mp="39rregohlw16k">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-1311" data-mp="3esyqizqs7mj1">Original Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-1312" data-mp="3jr04j69covnn">{selected.resubmitReason || selected.makerReason}</Typography>
                  <Typography variant="caption" color="#EF4444" fontWeight={600} data-id="element-1313" data-mp="3ovu75q704win">Checker's Rejection Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-1314" data-mp="3jvpp3vqg3tqb">{selected.checkerComment}</Typography>
                </Paper>
              </>}
            <TextField label={`Reason for ${actionType === 'resubmit' ? 'Resubmission' : 'Deleting'}`} placeholder={`Enter reason for ${actionType === 'resubmit' ? 'resubmitting' : 'deleting'} this request...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-1315" data-mp="3fwz2iijlvuhg" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1316" data-mp="2nkwl01qyxct">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-1317" data-mp="z68nxctovh9v">Cancel</Button>
          <Button variant="contained" color={actionType === 'resubmit' ? 'primary' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-1319" data-mp="1e29gb460yhrr" /> : <Icon icon={actionType === 'resubmit' ? 'lucide:send' : 'lucide:trash'} width={16} data-id="element-1320" data-mp="1iustw14ppcwk" />} data-id="element-1318" data-mp="ufjy01xnqscu">
            {actionType === 'resubmit' ? 'Resubmit' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default MakerBusinessKeywordTable;